<template>
  <div class="crm-dashboard">
    <b-row
      v-if="isAdmin || isCompanyAssociatePartner"
      class="portlet-container row-spacing"
    >
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet portlet--global-filters">
          <div class="portlet-header" style="border-radius: 12px 12px 0 0;">
            <h3 class="text-white mb-0">Global Filters</h3>
          </div>
          <b-card-body class="portlet-body">
            <GlobalFilterBar
              ref="globalFilterBar"
              :base-api="baseApi"
              :access-token="accessToken"
              :show-channel-partner-filter="isAdmin"
              :channel-partner-scope-id="companyAssociatePartnerScopeId"
              @filters-changed="handleFiltersChanged"
              @search="handleSearch"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row
      v-if="summaryCards.length"
      class="portlet-container row-spacing"
    >
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet">
          <div class="portlet-header">
            <h3 class="text-white mb-0">CP Summary</h3>
          </div>
          <b-card-body class="portlet-body kpi-panel-body">
            <b-alert v-if="fetchError" show variant="danger" class="mb-2">
              {{ fetchError }}
            </b-alert>
            <div v-if="isLoading" class="dashboard-loading text-center py-4">
              <b-spinner variant="primary" label="Loading dashboard" />
              <p class="text-muted mb-0 mt-2">Loading summary…</p>
            </div>
            <b-row v-else class="summary-kpi-row">
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                xl="2"
                class="summary-kpi-col"
              >
                <div
                  class="summary-kpi-clickable"
                  role="button"
                  tabindex="0"
                  @click="handleSummaryCardClick(card.key)"
                  @keyup.enter="handleSummaryCardClick(card.key)"
                >
                  <kpi-card
                    :title="card.title"
                    :value="card.value"
                    :subtitle="card.subtitle"
                    :color="card.color"
                    :info-text="card.infoText"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <DashboardDetailModal
      :widget-title="detailWidgetTitle"
      :detail-endpoint="detailEndpoint"
      :detail-params="detailParams"
      :filters="dashboardFilters"
      :visible.sync="detailModalVisible"
      @modal-closed="closeDetailModal"
    />
  </div>
</template>

<script>
//
import { dashboardGet } from '@/components/dashboard/dashboardApi';
import moment from 'moment';
import { BRow, BCol, BCard, BCardBody, BSpinner, BAlert } from 'bootstrap-vue';
import GlobalFilterBar from '@/components/dashboard/GlobalFilterBar.vue';
import KpiCard from '@/components/dashboard/KpiCard.vue';
import DashboardDetailModal from '@/components/dashboard/DashboardDetailModal.vue';

/** CP summary card key → dedicated CP dashboard detail API. */
const SUMMARY_CARD_DETAIL_MAP = {
  total_customer: {
    title: 'Total Customer',
    endpoint: '/dashboard/cp/detail/total_customer',
  },
  site_visit_done: {
    title: 'Site Visit Done',
    endpoint: '/dashboard/cp/detail/site_visit_done',
  },
  negotiation: {
    title: 'Negotiation',
    endpoint: '/dashboard/cp/detail/negotiation',
  },
  closure: {
    title: 'Closure',
    endpoint: '/dashboard/cp/detail/closure',
  },
  token: {
    title: 'Token',
    endpoint: '/dashboard/cp/detail/token',
  },
  commission: {
    title: 'Commission',
    endpoint: '/dashboard/cp/detail/commission',
  },
  interested: {
    title: 'Interested',
    endpoint: '/dashboard/cp/detail/interested',
  },
  site_visit_interested: {
    title: 'Site Visit Interested',
    endpoint: '/dashboard/cp/detail/site_visit_interested',
  },
  closure_meeting: {
    title: 'Closure Meeting',
    endpoint: '/dashboard/cp/detail/closure_meeting',
  },
  total_payment_revenue: {
    title: 'Total Payment / Revenue',
    endpoint: '/dashboard/cp/detail/total_payment_revenue',
  },
};

const SUMMARY_CARD_LOGIC_MAP = {
  total_customer: 'Count of unique customers created for selected date range and selected CP scope.',
  site_visit_done: 'Count of customers/leads where site visit status is marked done in selected filters.',
  negotiation: 'Count of active records currently in negotiation stage.',
  closure: 'Count of records marked as closed/closure in selected range.',
  token: 'Count of deals where token amount/booking token entry exists.',
  commission: 'Sum of commission value for selected scope and date filters; subtitle shows number of commission-linked leads.',
  interested: 'Count of leads marked as interested in follow-up/status workflow.',
  site_visit_interested: 'Count of interested leads that also reached site-visit-interested stage.',
  closure_meeting: 'Count of leads/deals currently in closure meeting stage.',
  total_payment_revenue: 'Sum of payment/revenue amount captured for selected CP and date filters.',
};

const emptyCpSummary = () => ({
  total_customer: { count: 0 },
  site_visit_done: { count: 0 },
  negotiation: { count: 0 },
  closure: { count: 0 },
  token: { count: 0 },
  commission: { count: 0, value: 0 },
  interested: { count: 0 },
  site_visit_interested: { count: 0 },
  closure_meeting: { count: 0 },
  total_payment_revenue: { count: 0, value: 0 },
});

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch (e) {
    return {};
  }
}

function isAdminUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'admin' || type === 'admin';
}

function isCompanyAssociatePartnerUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'companyassociatepartner' || type === 'companyassociatepartner';
}

export default {
  name: 'CrmDashboard',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BSpinner,
    BAlert,
    GlobalFilterBar,
    KpiCard,
    DashboardDetailModal,
  },
  data() {
    return {
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem('accessToken'),
      filters: {
        date_from: moment().startOf('month').format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
      },
      cpSummary: emptyCpSummary(),
      isLoading: false,
      initialFetched: false,
      _fetchTimer: null,
      detailModalVisible: false,
      detailWidgetTitle: '',
      detailEndpoint: '',
      detailParams: {},
      fetchError: null,
    };
  },
  computed: {
    isAdmin() {
      return isAdminUser(readUserData());
    },
    isCompanyAssociatePartner() {
      return isCompanyAssociatePartnerUser(readUserData());
    },
    companyAssociatePartnerScopeId() {
      if (!this.isCompanyAssociatePartner) {
        return null;
      }
      const cpId = Number(readUserData().id);
      return Number.isFinite(cpId) && cpId > 0 ? cpId : null;
    },
    /** API params: CP login always scopes to their user id (+ downline on server). */
    dashboardFilters() {
      const payload = { ...this.filters };
      if (this.companyAssociatePartnerScopeId) {
        payload.channel_partner_id = this.companyAssociatePartnerScopeId;
      }
      return payload;
    },
    summaryCards() {
      const s = this.cpSummary;
      const commissionCount = Number(s.commission?.count) || 0;
      const commissionValue = Number(s.commission?.value) || 0;

      return [
        {
          key: 'total_customer',
          title: 'Total Customer',
          value: this.formatCount(s.total_customer?.count),
          color: 'blue darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.total_customer,
        },
        {
          key: 'site_visit_done',
          title: 'Site Visit Done',
          value: this.formatCount(s.site_visit_done?.count),
          color: 'cyan darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.site_visit_done,
        },
        {
          key: 'negotiation',
          title: 'Negotiation',
          value: this.formatCount(s.negotiation?.count),
          color: 'amber darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.negotiation,
        },
        {
          key: 'closure',
          title: 'Closure',
          value: this.formatCount(s.closure?.count),
          color: 'purple darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.closure,
        },
        {
          key: 'token',
          title: 'Token',
          value: this.formatCount(s.token?.count),
          color: 'teal darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.token,
        },
        {
          key: 'commission',
          title: 'Commission',
          value: this.formatCurrency(commissionValue),
          subtitle: `${this.formatCount(commissionCount)} leads with commission`,
          color: 'blue-grey darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.commission,
        },
        {
          key: 'interested',
          title: 'Interested',
          value: this.formatCount(s.interested?.count),
          color: 'green darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.interested,
        },
        {
          key: 'site_visit_interested',
          title: 'Site Visit Interested',
          value: this.formatCount(s.site_visit_interested?.count),
          color: 'orange darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.site_visit_interested,
        },
        {
          key: 'closure_meeting',
          title: 'Closure Meeting',
          value: this.formatCount(s.closure_meeting?.count),
          color: 'light-green darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.closure_meeting,
        },
        {
          key: 'total_payment_revenue',
          title: 'Total Payment / Revenue',
          value: this.formatCurrency(s.total_payment_revenue?.value),
          color: 'indigo darken-1',
          infoText: SUMMARY_CARD_LOGIC_MAP.total_payment_revenue,
        },
      ];
    },
  },
  mounted() {
    // GlobalFilterBar emits filters on mount; fetch runs from handleFiltersChanged.
    this.$nextTick(() => {
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
      }
    });
  },
  activated() {
    this.fetchSummaryData();
  },
  beforeDestroy() {
    if (this._fetchTimer) {
      clearTimeout(this._fetchTimer);
    }
  },
  methods: {
    formatCount(value) {
      return Number(value || 0).toLocaleString('en-IN');
    },
    formatCurrency(value) {
      const n = Number(value || 0);
      return `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    },
    formatRateSubtitle(rate, suffix) {
      if (rate === null || rate === undefined || rate === '') {
        return null;
      }
      const n = Number(rate);
      if (Number.isNaN(n)) {
        return null;
      }
      return `${n.toFixed(2)}% ${suffix}`;
    },
    handleFiltersChanged(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
        return;
      }
      this.debouncedFetch();
    },
    handleSearch(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.debouncedFetch(true);
    },
    debouncedFetch(forceRefresh = false) {
      if (this._fetchTimer) {
        clearTimeout(this._fetchTimer);
      }
      this._fetchTimer = setTimeout(() => {
        this._fetchTimer = null;
        this.fetchSummaryData(forceRefresh);
      }, 400);
    },
    handleSummaryCardClick(cardKey) {
      const config = SUMMARY_CARD_DETAIL_MAP[cardKey];
      if (!config) {
        return;
      }
      this.openDetailModal(config.title, config.endpoint);
    },
    openDetailModal(widgetTitle, detailEndpoint, detailParams = {}) {
      this.detailWidgetTitle = widgetTitle;
      this.detailEndpoint = detailEndpoint;
      this.detailParams = detailParams;
      this.detailModalVisible = true;
    },
    closeDetailModal() {
      this.detailModalVisible = false;
      this.detailWidgetTitle = '';
      this.detailEndpoint = '';
      this.detailParams = {};
    },
    async fetchSummaryData(forceRefresh = false) {
      if (!this.baseApi || !this.accessToken) {
        return;
      }
      this.isLoading = true;
      this.fetchError = null;
      try {
        const params = { ...this.dashboardFilters };
        if (forceRefresh) {
          params.refresh = 1;
        }
        const response = await dashboardGet(
          this.baseApi,
          this.accessToken,
          '/dashboard/cp/all',
          params
        );
        const data = response.data?.data || {};
        this.cpSummary = {
          ...emptyCpSummary(),
          ...data,
        };
      } catch (error) {
        console.error('Error fetching CP summary:', error);
        this.fetchError =
          error.response?.data?.message || error.message || 'Failed to load CP dashboard summary';
        this.cpSummary = emptyCpSummary();
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.crm-dashboard {
  padding: 20px;
}

.row-spacing {
  margin-top: 20px;
  margin-bottom: 20px;
}

.portlet {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.portlet.portlet--global-filters {
  overflow: visible;
}

.portlet.portlet--global-filters .portlet-body {
  overflow: visible;
}

.portlet:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.portlet-header {
  padding: 10px 16px;
  background: linear-gradient(135deg, #4e73df, #224abe);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.portlet-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.portlet-body {
  padding: 20px;
  flex-grow: 1;
}

.kpi-panel-body {
  padding: 8px 12px 16px;
}

.summary-kpi-row {
  margin-left: -6px;
  margin-right: -6px;
}

.summary-kpi-col {
  padding-left: 6px;
  padding-right: 6px;
  margin-bottom: 12px;
}

.summary-kpi-clickable {
  cursor: pointer;
  height: 100%;
}

.summary-kpi-clickable:focus {
  outline: 2px solid #4e73df;
  outline-offset: 2px;
  border-radius: 8px;
}
</style>
